import { useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { usePCAStore } from '../stores/pcaStore';
import { DimensionBar } from './DimensionBar';
import { getColorForCluster, getClusterName } from '../utils/colorMapping';
import { getGuestEpisodeDate, formatEpisodeDate, getEpisodeLinks } from '../utils/guestDates';
import { loadEpisodeDescriptions, getGuestEpisodes, hasMultipleEpisodes, type EpisodeInfo } from '../utils/episodeDescriptions';
import fireplaceLogo from '../assets/fireplace-logo.png';

type SortMode = 'dimension' | 'value-high' | 'value-low' | 'absolute';

// Empty State Component
function EmptyState() {
  const { setQuizActive } = usePCAStore();
  
  return (
    <div className="text-center flex flex-col items-center justify-center h-full px-6">
      {/* Fireplace Logo - faded */}
      <img
        src={fireplaceLogo}
        alt="Lenny's Podcast"
        className="w-16 h-16 mx-auto mb-4"
        style={{
          filter: 'grayscale(40%) brightness(0.7) sepia(20%)',
          opacity: 0.4,
        }}
      />
      
      {/* Guidance text */}
      <p className="text-base font-semibold mb-2 text-text-primary">Who do you want to explore?</p>
      
      {/* Action suggestions */}
      <div className="space-y-2 text-sm text-text-secondary mb-4">
        <p className="flex items-center justify-center gap-2">
          <span className="text-orange-primary">👆</span>
          Click any dot in the 3D space
        </p>
        <p className="flex items-center justify-center gap-2">
          <span className="text-orange-primary">🔍</span>
          Search for a PM at the top
        </p>
      </div>
      
      {/* Quiz CTA */}
      <div className="pt-3 border-t border-warm-border/50 w-full">
        <p className="text-xs text-text-muted mb-2">Or discover where you fit:</p>
        <button
          onClick={() => setQuizActive(true)}
          className="px-4 py-2 bg-orange-primary/10 hover:bg-orange-primary/20 text-orange-primary rounded-lg text-sm font-medium transition-colors border border-orange-primary/20"
        >
          🧭 Take the Quiz
        </button>
      </div>
    </div>
  );
}

// Side panel icon
function SidePanelIcon({ className = '' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M15 3v18" />
    </svg>
  );
}

interface ProfilePanelProps {
  onCollapse?: () => void;
}

// Cluster descriptions - what characterizes each cluster
const CLUSTER_DESCRIPTIONS: Record<number, string> = {
  0: "PMs focused on growth strategies, acquisition, and go-to-market execution. Strong emphasis on metrics, experimentation, and scaling products to market.",
  1: "Product builders and technical leaders who dive deep into engineering, design systems, and product craft. Focus on building exceptional products from the ground up.",
  2: "Strategic thinkers and executives who focus on leadership, organizational design, and company building. Often discuss team management and culture.",
  3: "Sales, partnerships, and business development focused PMs. Strong emphasis on revenue, enterprise sales, and B2B strategies.",
  4: "Consumer product specialists focused on user experience, engagement, and retention. Deep expertise in user psychology and product-market fit.",
  5: "Data-driven PMs who emphasize analytics, metrics frameworks, and quantitative decision making. Focus on measurement and optimization.",
  6: "Founders and entrepreneurs sharing startup lessons, fundraising insights, and early-stage product development strategies.",
  7: "Platform and infrastructure PMs who focus on developer experience, APIs, and technical product management.",
  8: "Design-focused PMs who emphasize user research, design thinking, and creating delightful user experiences.",
  9: "Operations and process-oriented PMs focused on efficiency, workflows, and scaling organizational capabilities."
};

export function ProfilePanel({ onCollapse }: ProfilePanelProps) {
  const { 
    selectedGuest, 
    hoveredGuest, 
    comparisonGuest,
    comparisonMode,
    comparisonFirstGuest,
    selectedCluster,
    setSelectedCluster,
    pcaData,
    axisSelection,
    getGuestByName,
    getGuestCluster,
    getDimensionRange,
    getDimensionName,
    getGuestDifferences,
    getAllGuestDifferences,
    getDistancePercentile,
    setComparisonGuest,
    addSlice,
    userCoordinates
  } = usePCAStore();

  const [sortMode, setSortMode] = useState<SortMode>('dimension');
  const [episodeDescriptions, setEpisodeDescriptions] = useState<Record<string, any>>({});
  const [selectedEpisodeIndex, setSelectedEpisodeIndex] = useState<number>(0);

  // Load episode descriptions on mount
  useEffect(() => {
    loadEpisodeDescriptions().then(setEpisodeDescriptions);
  }, []);

  // Get the current guest to display
  const guestName = selectedGuest || hoveredGuest;
  const isUser = guestName === '__USER__';
  const guest = isUser ? null : (guestName ? getGuestByName(guestName) : null);

  // Reset episode index when guest changes
  useEffect(() => {
    setSelectedEpisodeIndex(0);
  }, [guestName]);

  // Get episode info for current guest
  const guestEpisodes = useMemo(() => {
    if (!guestName || Object.keys(episodeDescriptions).length === 0) return [];
    return getGuestEpisodes(guestName, episodeDescriptions);
  }, [guestName, episodeDescriptions]);

  const hasMultiple = useMemo(() => {
    if (!guestName || Object.keys(episodeDescriptions).length === 0) return false;
    return hasMultipleEpisodes(guestName, episodeDescriptions);
  }, [guestName, episodeDescriptions]);

  const currentEpisode: EpisodeInfo | null = useMemo(() => {
    if (guestEpisodes.length === 0) return null;
    return guestEpisodes[selectedEpisodeIndex] || guestEpisodes[0];
  }, [guestEpisodes, selectedEpisodeIndex]);

  // Build dimension data with ranges
  const dimensionData = useMemo(() => {
    // For user profile, use userCoordinates
    if (isUser && userCoordinates && pcaData) {
      return userCoordinates.map((value, index) => {
        const dimension = index + 1;
        const range = getDimensionRange(dimension);
        const isXAxis = axisSelection.x === dimension;
        const isYAxis = axisSelection.y === dimension;
        const isZAxis = axisSelection.z === dimension;
        const dimName = getDimensionName(dimension);
        
        return {
          dimension,
          value,
          min: range.min,
          max: range.max,
          isAxisDimension: isXAxis || isYAxis || isZAxis,
          axisLabel: isXAxis ? '1' as const : isYAxis ? '2' as const : isZAxis ? '3' as const : undefined,
          name: dimName
        };
      });
    }
    
    // For guest profile, use guest coordinates
    if (!guest || !pcaData) return [];

    return guest.coordinates.map((value, index) => {
      const dimension = index + 1;
      const range = getDimensionRange(dimension);
      const isXAxis = axisSelection.x === dimension;
      const isYAxis = axisSelection.y === dimension;
      const isZAxis = axisSelection.z === dimension;
      const dimName = getDimensionName(dimension);
      
      return {
        dimension,
        value,
        min: range.min,
        max: range.max,
        isAxisDimension: isXAxis || isYAxis || isZAxis,
        axisLabel: isXAxis ? '1' as const : isYAxis ? '2' as const : isZAxis ? '3' as const : undefined,
        name: dimName
      };
    });
  }, [guest, pcaData, axisSelection, getDimensionRange, getDimensionName, isUser, userCoordinates]);

  // Sort dimensions
  const sortedDimensions = useMemo(() => {
    // Sort
    switch (sortMode) {
      case 'value-high':
        return [...dimensionData].sort((a, b) => b.value - a.value);
      case 'value-low':
        return [...dimensionData].sort((a, b) => a.value - b.value);
      case 'absolute':
        return [...dimensionData].sort((a, b) => Math.abs(b.value) - Math.abs(a.value));
      case 'dimension':
      default:
        return dimensionData;
    }
  }, [dimensionData, sortMode]);

  // Handle dimension click - could set as axis or add as slice
  const handleDimensionClick = (dimension: number) => {
    // If not already an axis, offer to add as slice
    const isAxis = dimension === axisSelection.x || 
                   dimension === axisSelection.y || 
                   dimension === axisSelection.z;
    
    if (!isAxis) {
      addSlice(dimension);
    }
  };

  return (
    <div className="h-full flex flex-col bg-warm-surface/30">
      {guestName ? (
        <>
          {/* Header - Minimalistic */}
          <div className="px-5 py-4 border-b border-warm-border/30 flex-shrink-0">
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1 min-w-0">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={guestName}
                    initial={{ opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 4 }}
                    className="space-y-2"
                  >
                  {/* Guest/User name - prominent */}
                  <h2 className="text-xl font-semibold text-text-primary truncate pr-2">
                    {isUser ? 'Beautiful You' : guestName}
                  </h2>
                  
                  {/* User grayed out links + message */}
                  {isUser && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-1.5">
                        {/* Grayed out Spotify */}
                        <div className="w-6 h-6 rounded-full bg-gray-300/50 text-gray-400 flex items-center justify-center opacity-40">
                          <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.42 1.56-.299.421-1.02.599-1.559.3z"/>
                          </svg>
                        </div>
                        {/* Grayed out YouTube */}
                        <div className="w-6 h-6 rounded-full bg-gray-300/50 text-gray-400 flex items-center justify-center opacity-40">
                          <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                          </svg>
                        </div>
                        {/* Grayed out Lenny's Newsletter */}
                        <div className="w-6 h-6 rounded-full bg-gray-300/50 flex items-center justify-center opacity-40 overflow-hidden">
                          <img 
                            src={fireplaceLogo} 
                            alt="Lenny's Newsletter" 
                            className="w-full h-full object-contain p-1 grayscale"
                          />
                        </div>
                      </div>
                      <p className="text-xs text-text-secondary/60 italic leading-relaxed">
                        Perhaps one day you'll join the sacred list of Lenny's guests
                      </p>
                    </div>
                  )}
                  
                  {/* Episode description - one-liner (only for guests) */}
                  {!isUser && currentEpisode?.description && (
                    <p className="text-sm text-text-secondary/80 leading-relaxed line-clamp-2">
                      {currentEpisode.description}
                    </p>
                  )}
                  
                  {/* Episode selector for multiple episodes (only for guests) */}
                  {!isUser && hasMultiple && guestEpisodes.length > 1 && (
                    <div className="flex items-center gap-2 pt-1">
                      <select
                        value={selectedEpisodeIndex}
                        onChange={(e) => setSelectedEpisodeIndex(Number(e.target.value))}
                        className="text-xs bg-warm-surface/50 border border-warm-border/50 rounded-md px-2 py-1 text-text-primary focus:outline-none focus:ring-2 focus:ring-orange-primary/20 focus:border-orange-primary/50"
                      >
                        {guestEpisodes.map((episode, index) => (
                          <option key={index} value={index}>
                            {episode.date ? `Episode ${index + 1} (${episode.date})` : `Episode ${index + 1}`}
                          </option>
                        ))}
                      </select>
                      <span className="text-xs text-text-secondary/60">
                        {guestEpisodes.length} episodes
                      </span>
                    </div>
                  )}
                  
                  {/* Episode date and links - compact single line (only for guests) */}
                  {!isUser && (() => {
                    const episodeDate = getGuestEpisodeDate(guestName);
                    const formattedDate = formatEpisodeDate(episodeDate);
                    const links = getEpisodeLinks(guestName);
                    
                    return (
                      <div className="flex items-center gap-3">
                        {formattedDate && (
                          <span className="text-xs text-text-secondary/80 font-medium">
                            {formattedDate}
                          </span>
                        )}
                        <div className="flex items-center gap-1.5">
                          <a
                            href={links.spotify}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-6 h-6 rounded-full bg-[#1DB954] hover:bg-[#1ed760] text-white flex items-center justify-center transition-all shadow-sm hover:shadow-md hover:scale-110"
                            title="Listen on Spotify"
                          >
                            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.42 1.56-.299.421-1.02.599-1.559.3z"/>
                            </svg>
                          </a>
                          <a
                            href={links.youtube}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-6 h-6 rounded-full bg-[#FF0000] hover:bg-[#FF3333] text-white flex items-center justify-center transition-all shadow-sm hover:shadow-md hover:scale-110"
                            title="Watch on YouTube"
                          >
                            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                            </svg>
                          </a>
                          <a
                            href={links.newsletter}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-6 h-6 rounded-full bg-warm-surface-hover hover:bg-warm-border text-text-primary flex items-center justify-center transition-all border border-warm-border/50 shadow-sm hover:shadow-md hover:scale-110 overflow-hidden"
                            title="View on Lenny's Newsletter"
                          >
                            <img 
                              src={fireplaceLogo} 
                              alt="Lenny's Newsletter" 
                              className="w-full h-full object-contain p-1"
                            />
                          </a>
                        </div>
                      </div>
                    );
                  })()}
                  
                  {/* Cluster badge - subtle (only for guests) */}
                  {!isUser && getGuestCluster(guestName) && (
                    <div className="flex items-center gap-2 pt-1">
                      <span 
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: getColorForCluster(getGuestCluster(guestName)) }}
                      />
                      <span className="text-xs text-text-secondary/70">
                        {getClusterName(getGuestCluster(guestName)!)}
                      </span>
                    </div>
                  )}
                  </motion.div>
                </AnimatePresence>
              </div>
              {/* Collapse button */}
              {onCollapse && (
                <button
                  onClick={onCollapse}
                  className="p-1.5 rounded-md hover:bg-warm-surface-hover/50 transition-colors flex-shrink-0 ml-2"
                  title="Collapse panel"
                >
                  <SidePanelIcon className="w-4 h-4 text-text-secondary/60" />
                </button>
              )}
            </div>
          </div>
        </>
      ) : selectedCluster !== null ? (
        /* Cluster Selected View */
        <div className="flex-1 flex flex-col">
          <div className="px-5 py-4 border-b border-warm-border/30 flex-shrink-0">
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2">
                  <span 
                    className="w-4 h-4 rounded-full border border-black/15"
                    style={{ backgroundColor: getColorForCluster(selectedCluster) }}
                  />
                  <h2 className="text-lg font-semibold text-text-primary">
                    {getClusterName(selectedCluster)}
                  </h2>
                </div>
                <p className="text-xs text-text-secondary/60 uppercase tracking-wide">
                  Cluster {selectedCluster + 1} of 10
                </p>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setSelectedCluster(null)}
                  className="p-1.5 rounded-md hover:bg-warm-surface-hover/50 transition-colors flex-shrink-0"
                  title="Clear cluster selection"
                >
                  <svg className="w-4 h-4 text-text-secondary/60" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
                {onCollapse && (
                  <button
                    onClick={onCollapse}
                    className="p-1.5 rounded-md hover:bg-warm-surface-hover/50 transition-colors flex-shrink-0"
                    title="Collapse panel"
                  >
                    <SidePanelIcon className="w-4 h-4 text-text-secondary/60" />
                  </button>
                )}
              </div>
            </div>
          </div>
          
          {/* Cluster Description */}
          <div className="px-5 py-4 flex-1">
            <h3 className="text-xs text-text-secondary/60 uppercase tracking-wide font-medium mb-2">
              What characterizes this cluster
            </h3>
            <p className="text-sm text-text-secondary leading-relaxed">
              {CLUSTER_DESCRIPTIONS[selectedCluster] || "PMs in this cluster share common themes and approaches to product management."}
            </p>
            
            {/* Guests in cluster count */}
            {pcaData && (
              <div className="mt-6 pt-4 border-t border-warm-border/30">
                <p className="text-xs text-text-secondary/60">
                  <span className="font-semibold text-text-primary">
                    {pcaData.guestCoordinates.filter(g => getGuestCluster(g.guest) === selectedCluster).length}
                  </span>
                  {' '}guests in this cluster
                </p>
              </div>
            )}
            
            {/* Dimensionality note */}
            <div className="mt-4 p-3 bg-orange-primary/5 border border-orange-primary/20 rounded-lg">
              <p className="text-xs text-text-primary/80 leading-relaxed">
                <span className="font-medium">Why don't these look clustered?</span> Our human brains see 3 dimensions, but this space (Latent Lenny) lives in 20. These guests are neighbors in hyperspace—you'll have to trust the math on this one. 🧠✨
              </p>
            </div>
          </div>
        </div>
      ) : (
        /* Empty State */
        <div className="flex-1 flex flex-col">
          {/* Header with collapse button */}
          {onCollapse && (
            <div className="px-5 py-3 border-b border-warm-border/30 flex-shrink-0 flex justify-end">
              <button
                onClick={onCollapse}
                className="p-1.5 rounded-md hover:bg-warm-surface-hover/50 transition-colors"
                title="Collapse panel"
              >
                <SidePanelIcon className="w-4 h-4 text-text-secondary/60" />
              </button>
            </div>
          )}
          <EmptyState />
        </div>
      )}

      {/* Sort controls - Minimalistic */}
      {(guest || (isUser && userCoordinates)) && (
        <div className="px-5 py-2.5 border-b border-warm-border/30 flex-shrink-0 bg-warm-surface/20">
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] text-text-secondary/60 uppercase tracking-wider font-medium mr-1">
              Sort:
            </span>
            {[
              { mode: 'dimension' as const, label: 'Order' },
              { mode: 'absolute' as const, label: 'Impact' },
              { mode: 'value-high' as const, label: 'High' },
              { mode: 'value-low' as const, label: 'Low' },
            ].map(({ mode, label }) => (
              <button
                key={mode}
                onClick={() => setSortMode(mode)}
                className={`px-2 py-0.5 text-[10px] rounded-md transition-all ${
                  sortMode === mode
                    ? 'bg-orange-primary/20 text-orange-primary font-medium'
                    : 'text-text-secondary/60 hover:text-text-primary hover:bg-warm-surface-hover/50'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Comparison View - Redesigned */}
      {selectedGuest && comparisonGuest && (() => {
        // Use getAllGuestDifferences if in comparison mode, otherwise use getGuestDifferences
        const isCompareMode = comparisonMode === 'idle' && comparisonFirstGuest !== null;
        const differences = isCompareMode && comparisonFirstGuest 
          ? getAllGuestDifferences(comparisonFirstGuest, comparisonGuest)
          : getGuestDifferences(selectedGuest, comparisonGuest);
        
        if (!differences) return null;
        
        // Handle __USER__ case - don't require guest object for user
        const guest1Name = isCompareMode && comparisonFirstGuest ? comparisonFirstGuest : selectedGuest;
        const guest2Name = comparisonGuest;
        const isGuest2User = guest2Name === '__USER__';
        
        const guest1 = isCompareMode && comparisonFirstGuest 
          ? getGuestByName(comparisonFirstGuest)
          : getGuestByName(selectedGuest);
        
        // Only require guest1 to exist (guest2 can be user)
        if (!guest1 && guest1Name !== '__USER__') return null;

        // Display names - show "Beautiful You" for __USER__
        const displayName1 = guest1Name === '__USER__' ? 'Beautiful You' : guest1Name;
        const displayName2 = isGuest2User ? 'Beautiful You' : guest2Name;
        const allDiffs = 'allDifferences' in differences ? differences.allDifferences : differences.topDifferences;
        
        // Calculate percentile score (0-100)
        const percentile = getDistancePercentile(differences.distance);
        
        // Get interpretation text based on percentile
        const getInterpretation = (p: number) => {
          if (p >= 90) return { text: 'Extremely different', color: 'text-red-400' };
          if (p >= 75) return { text: 'Very different', color: 'text-orange-primary' };
          if (p >= 50) return { text: 'Moderately different', color: 'text-yellow-500' };
          if (p >= 25) return { text: 'Somewhat similar', color: 'text-green-400' };
          return { text: 'Very similar', color: 'text-emerald-400' };
        };
        const interpretation = getInterpretation(percentile);

        return (
          <div className="px-5 py-4 border-b border-warm-border/30 flex-shrink-0 bg-gradient-to-r from-orange-primary/5 to-transparent">
            {/* Header with clear button */}
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-xs font-semibold text-text-secondary/60 uppercase tracking-wider">
                {isCompareMode ? 'PM Comparison' : 'Most Different PM'}
              </h4>
              <button
                onClick={() => setComparisonGuest(null)}
                className="text-xs text-text-secondary/60 hover:text-text-primary transition-colors px-2 py-1 rounded hover:bg-warm-surface-hover/50"
              >
                Clear
              </button>
            </div>
            
            {/* Two-sided pill showing both PMs */}
            <div className="flex items-stretch rounded-xl overflow-hidden border border-warm-border/50 mb-4">
              {/* PM 1 side */}
              <div className="flex-1 px-4 py-3 bg-orange-primary/10 border-r border-warm-border/30">
                <div className="text-[10px] text-orange-primary/70 uppercase tracking-wider mb-1 font-medium">
                  {guest1Name === '__USER__' ? 'You' : 'PM 1'}
                </div>
                <div className="text-sm font-semibold text-text-primary truncate" title={displayName1}>
                  {displayName1}
                </div>
                {guest1Name !== '__USER__' && getGuestCluster(guest1Name) && (
                  <div className="flex items-center gap-1.5 mt-1.5">
                    <span 
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: getColorForCluster(getGuestCluster(guest1Name)) }}
                    />
                    <span className="text-[10px] text-text-secondary/70">{getClusterName(getGuestCluster(guest1Name)!)}</span>
                  </div>
                )}
              </div>
              
              {/* Divider with "vs" */}
              <div className="flex items-center justify-center w-10 bg-warm-surface/50 text-text-secondary/40 text-xs font-medium">
                vs
              </div>
              
              {/* PM 2 side */}
              <div className="flex-1 px-4 py-3 bg-blue-500/10">
                <div className="text-[10px] text-blue-400/70 uppercase tracking-wider mb-1 font-medium">
                  {isGuest2User ? 'You' : 'PM 2'}
                </div>
                <div className="text-sm font-semibold text-text-primary truncate" title={displayName2}>
                  {displayName2}
                </div>
                {!isGuest2User && getGuestCluster(guest2Name) && (
                  <div className="flex items-center gap-1.5 mt-1.5">
                    <span 
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: getColorForCluster(getGuestCluster(guest2Name)) }}
                    />
                    <span className="text-[10px] text-text-secondary/70">{getClusterName(getGuestCluster(guest2Name)!)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Difference Score - Percentile based */}
            <div className="bg-warm-surface/30 rounded-xl p-4 mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] text-text-secondary/60 uppercase tracking-wider font-medium">
                  Difference Score
                </span>
                <span className={`text-xs font-medium ${interpretation.color}`}>
                  {interpretation.text}
                </span>
              </div>
              
              {/* Score display */}
              <div className="flex items-end gap-2 mb-3">
                <span className="text-4xl font-bold text-orange-primary">{percentile}</span>
                <span className="text-lg text-text-secondary/60 pb-1">/100</span>
              </div>
              
              {/* Progress bar */}
              <div className="h-2 bg-warm-border/30 rounded-full overflow-hidden">
                <div 
                  className="h-full rounded-full transition-all duration-500 ease-out"
                  style={{ 
                    width: `${percentile}%`,
                    background: percentile >= 75 
                      ? 'linear-gradient(90deg, #f97316, #ef4444)' 
                      : percentile >= 50 
                        ? 'linear-gradient(90deg, #eab308, #f97316)'
                        : 'linear-gradient(90deg, #22c55e, #eab308)'
                  }}
                />
              </div>
              
              {/* Context */}
              <p className="text-[11px] text-text-secondary/50 mt-2">
                More different than {percentile}% of all PM pairs
              </p>
            </div>

            {/* Differences - show all if in compare mode, top 5 otherwise */}
            <div>
              <div className="text-[10px] text-text-secondary/60 uppercase tracking-wider mb-2 font-medium">
                {isCompareMode ? 'All Dimension Differences' : 'Top Differences'}
              </div>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {allDiffs.map((diff) => (
                  <div key={diff.dimension} className="text-xs">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-text-primary font-medium">{diff.name}</span>
                      <span className="text-orange-primary font-semibold">Δ{diff.diff.toFixed(2)}</span>
                    </div>
                    <div className="flex items-center gap-2 text-[10px] text-text-secondary/60">
                      <span>{displayName1}: <span className="font-medium">{diff.guest1Value.toFixed(2)}</span></span>
                      <span>•</span>
                      <span>{displayName2}: <span className="font-medium">{diff.guest2Value.toFixed(2)}</span></span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Dimension list - Better spacing */}
      <div className="flex-1 overflow-y-auto min-h-0">
        {(guest || (isUser && userCoordinates)) && !(comparisonGuest && comparisonMode === 'idle' && comparisonFirstGuest) ? (
          <div className="px-5 py-3 space-y-1">
            {sortedDimensions.map((dim) => (
              <DimensionBar
                key={dim.dimension}
                dimension={dim.dimension}
                value={dim.value}
                min={dim.min}
                max={dim.max}
                isAxisDimension={dim.isAxisDimension}
                axisLabel={dim.axisLabel}
                name={dim.name}
                onClick={() => handleDimensionClick(dim.dimension)}
              />
            ))}
          </div>
        ) : comparisonGuest && comparisonMode === 'idle' && comparisonFirstGuest ? (
          // Show absolute differences for all dimensions when comparing
          (() => {
            const allDiffs = getAllGuestDifferences(comparisonFirstGuest, comparisonGuest);
            if (!allDiffs) return null;
            
            return (
              <div className="px-5 py-3 space-y-1">
                {allDiffs.allDifferences.map((diff) => {
                  const range = getDimensionRange(diff.dimension);
                  return (
                    <DimensionBar
                      key={diff.dimension}
                      dimension={diff.dimension}
                      value={diff.diff} // Show absolute difference as the value
                      min={0}
                      max={range.max - range.min} // Max possible difference
                      isAxisDimension={axisSelection.x === diff.dimension || axisSelection.y === diff.dimension || axisSelection.z === diff.dimension}
                      axisLabel={axisSelection.x === diff.dimension ? '1' : axisSelection.y === diff.dimension ? '2' : axisSelection.z === diff.dimension ? '3' : undefined}
                      name={diff.name}
                      onClick={() => handleDimensionClick(diff.dimension)}
                    />
                  );
                })}
              </div>
            );
          })()
        ) : null}
      </div>

      {/* Footer - Minimalistic */}
      {(guest || (isUser && userCoordinates)) && (
        <div className="px-5 py-2.5 border-t border-warm-border/30 flex-shrink-0 bg-warm-surface/20">
          <div className="flex justify-between items-center text-[10px] text-text-secondary/60">
            <span className="uppercase tracking-wider">{sortedDimensions.length} dimensions</span>
            <span className="text-text-secondary/40">Click to slice</span>
          </div>
        </div>
      )}
    </div>
  );
}
